import { enterMutexHelper, exitMutexHelper } from './_panel-mutex-flag';
import { useArtifactStore } from './useArtifactStore';
import { useProjectHubStore } from './useProjectHubStore';
import type { RightPanelType } from './useRightDockStore';
import { useRightDockStore } from './useRightDockStore';

import { trackProductEvent } from '@/lib/analytics/product-events';

type PanelState = 'empty' | 'project-hub' | 'artifact-drawer' | 'global-dock';

function snapshot(): PanelState {
  if (useProjectHubStore.getState().isOpen) return 'project-hub';
  if (useRightDockStore.getState().activePanel) return 'global-dock';
  if (useArtifactStore.getState().isDrawerOpen) return 'artifact-drawer';
  return 'empty';
}

/**
 * PH-1: layout-level right-slot mutex.
 *
 * The chat page's right slot can hold one of three layout-occupying surfaces:
 * - Project Hub rail (fixed width, useProjectHubStore)
 * - Artifact drawer (resizable, useArtifactStore)
 * - Global right dock (resizable, useRightDockStore — engagement / drafts)
 *
 * One at a time. Every "open" path routes through a helper that closes the
 * other two atomically. This is SEPARATE from `_drawer-mutex.ts` which scopes
 * which body kind is inside the artifact drawer (image / document / strategy /
 * etc.). The two mutexes operate at different layers and never collide.
 *
 * Use these helpers everywhere a panel transitions to "open". `useRightDockStore`
 * also enforces this when legacy callers use `openPanel(x)` / `togglePanel(x)`;
 * the helper remains the preferred path because it centralizes telemetry.
 * Direct `setDrawerOpen(true)` is still unsafe and should route through
 * `openDrawer()`.
 *
 * Dev-mode invariant: the artifact drawer's `setDrawerOpen(true)` calls
 * `assertMutexHelperContext()` from this module — if any caller flips the
 * drawer open without going through `openDrawer()`, dev console warns. Mutex
 * helpers set `_inMutexHelper = true` while calling so the invariant skips
 * the warning for legitimate transitions.
 */

export function openHub(planId?: string | null): void {
  const from = snapshot();
  enterMutexHelper();
  try {
    useArtifactStore.getState().setDrawerOpen(false);
    useRightDockStore.getState().closePanel();
    useProjectHubStore.getState().setOpen(true, planId);
  } finally {
    exitMutexHelper();
  }
  void trackProductEvent('right_panel_state_transition', {
    from,
    to: 'project-hub',
    project_id: planId,
    trigger: 'user',
  });
}

export function openDrawer(): void {
  const from = snapshot();
  enterMutexHelper();
  try {
    useProjectHubStore.getState().setOpen(false);
    useRightDockStore.getState().closePanel();
    useArtifactStore.getState().setDrawerOpen(true);
  } finally {
    exitMutexHelper();
  }
  void trackProductEvent('right_panel_state_transition', {
    from,
    to: 'artifact-drawer',
    trigger: from === 'project-hub' ? 'user' : 'auto',
  });
}

export function openDock(
  panel: Exclude<RightPanelType, null>,
  /** Tab the engagement panel should land on — see `EngagementTabSeed`. */
  options?: { engagementTab?: string },
): void {
  const from = snapshot();
  enterMutexHelper();
  try {
    useProjectHubStore.getState().setOpen(false);
    useArtifactStore.getState().setDrawerOpen(false);
    useRightDockStore.getState().openPanel(panel, options);
  } finally {
    exitMutexHelper();
  }
  void trackProductEvent('right_panel_state_transition', {
    from,
    to: 'global-dock',
    panel,
    trigger: 'user',
  });
}

export function toggleDock(panel: Exclude<RightPanelType, null>): void {
  const from = snapshot();
  const activePanel = useRightDockStore.getState().activePanel;

  if (activePanel === panel) {
    useRightDockStore.getState().closePanel();
    void trackProductEvent('right_panel_state_transition', {
      from,
      to: 'empty',
      panel,
      trigger: 'user',
    });
    return;
  }

  openDock(panel);
}
